import type { Skill } from "./types";

const sleep = (ms: number) => new Promise((r) => setTimeout(r, ms));

type GuideStep = {
  label: string;
  targetTab?: string;
  selector?: string;
  highlightSelector?: string;
  /**
   * Si true, el step dispara un click DOM real (abre modales, botones, etc.)
   * además de la animación visual. Requiere que `selector` esté definido.
   */
  click?: boolean;
  waitMs?: number;
};

type Tutorial = {
  id: string;
  title: string;
  summary: string;
  steps: GuideStep[];
};

/**
 * Catálogo de tutoriales — muestran al user cómo hacer algo manualmente
 * desde la UI con cursor virtual + narración paso a paso. Ideal para
 * capacitación: el copiloto enseña en vez de solo ejecutar.
 *
 * Cada tutorial usa data-attrs estables del sidebar para apuntar al tab,
 * y narra los pasos siguientes que el user debe completar él mismo.
 */
const TUTORIALS: Record<string, Tutorial> = {
  // ─── PRODUCTOS Y CATÁLOGO ───────────────────────────────────────────
  "create-product": {
    id: "create-product",
    title: "Crear un producto manualmente",
    summary:
      "El producto se agrega desde el constructor de menú. Llena nombre, precio y categoría, y guarda.",
    steps: [
      { label: "Abrimos la pestaña Productos", targetTab: "productos", waitMs: 1000 },
      { label: "Aquí tienes el constructor de menú con tus categorías", waitMs: 1800 },
      { label: "Click en '+ Nuevo producto' al lado de la categoría destino", selector: "[data-copilot-action='create-product']", click: true, waitMs: 2000 },
      { label: "Llena el formulario: nombre, precio, descripción, tipo (cocina o on-demand)", waitMs: 3000 },
      { label: "Si hay imágenes, súbelas en la galería al final del formulario", waitMs: 2400 },
      { label: "Click en 'Guardar'. El producto aparece en la categoría elegida", waitMs: 2200 },
    ],
  },
  "edit-product": {
    id: "edit-product",
    title: "Editar un producto existente",
    summary:
      "Click sobre cualquier producto del catálogo y se abre su panel de edición a la derecha.",
    steps: [
      { label: "Abrimos Productos", targetTab: "productos", waitMs: 1000 },
      { label: "Click sobre el producto que quieres editar", waitMs: 2400 },
      { label: "Se abre el panel de edición con todos sus campos", waitMs: 2200 },
      { label: "Cambia los valores que necesites — los cambios se guardan al hacer click fuera o en 'Guardar'", waitMs: 2800 },
    ],
  },
  "create-category": {
    id: "create-category",
    title: "Crear una categoría del menú",
    summary: "Las categorías se crean desde el constructor de menú con el botón '+'.",
    steps: [
      { label: "Vamos a Productos", targetTab: "productos", waitMs: 1000 },
      { label: "Click en el botón '+ Nueva categoría' arriba del listado", waitMs: 2400 },
      { label: "Escribe el nombre (ej. Entradas, Postres, Bebidas)", waitMs: 2200 },
      { label: "Confirma. La categoría aparece como nueva pestaña/sección", waitMs: 2200 },
    ],
  },
  "configure-combo": {
    id: "configure-combo",
    title: "Configurar productos hijos de un combo",
    summary:
      "Para configurar un combo, abre el producto y agrega sus productos hijos en la sección 'Combo'.",
    steps: [
      { label: "Vamos a Productos", targetTab: "productos", waitMs: 1000 },
      { label: "Click en el producto que quieres convertir en combo", waitMs: 2400 },
      { label: "En el panel de edición busca la sección 'Combo' o 'Productos del combo'", waitMs: 2800 },
      { label: "Agrega los productos hijos y la cantidad de cada uno", waitMs: 2800 },
      { label: "Guarda los cambios", waitMs: 1800 },
    ],
  },
  "upload-product-image": {
    id: "upload-product-image",
    title: "Subir foto / video de un producto",
    summary: "Galería multimedia del producto desde su panel de edición.",
    steps: [
      { label: "Abrimos Productos", targetTab: "productos", waitMs: 1000 },
      { label: "Click en el producto", waitMs: 2200 },
      { label: "Baja hasta 'Galería multimedia' en el formulario", waitMs: 2400 },
      { label: "Click en '+' o 'Subir'. Selecciona el archivo desde tu equipo", waitMs: 2800 },
      { label: "La imagen se sube a Cloudinary. Recuerda guardar al final", waitMs: 2200 },
    ],
  },
  "reorder-products": {
    id: "reorder-products",
    title: "Reordenar productos en la carta",
    summary: "Arrastra los productos para cambiar su orden visual en la carta digital.",
    steps: [
      { label: "Vamos a Productos", targetTab: "productos", waitMs: 1000 },
      { label: "Encuentra el producto que quieres mover", waitMs: 2200 },
      { label: "Arrastra el producto desde su tarjeta y suéltalo en la nueva posición (drag & drop)", waitMs: 3200 },
      { label: "El orden se guarda automáticamente", waitMs: 1800 },
    ],
  },
  "weekly-schedule": {
    id: "weekly-schedule",
    title: "Configurar horario semanal de un producto",
    summary:
      "Define en qué días está disponible un producto y opcionalmente descuentos por día.",
    steps: [
      { label: "Vamos a Horario semanal", targetTab: "horario-semanal", waitMs: 1200 },
      { label: "A la izquierda hay un buscador de productos", waitMs: 2200 },
      { label: "Arrastra el producto a la celda del día donde estará disponible", waitMs: 2800 },
      { label: "Click en una celda para configurar descuento del día (porcentaje o monto fijo)", waitMs: 2800 },
      { label: "Los cambios se reflejan en la carta digital y al tomar pedidos", waitMs: 2200 },
    ],
  },

  // ─── PEDIDOS ──────────────────────────────────────────────────────────
  "take-order": {
    id: "take-order",
    title: "Tomar pedido en una mesa",
    summary:
      "Abre 'Tomar pedido', elige la mesa y agrega productos al carrito antes de confirmar.",
    steps: [
      { label: "Vamos a Tomar pedido", targetTab: "tomar-pedido", waitMs: 1200 },
      { label: "Aquí ves el grid de mesas. Las verdes están libres, las amarillas ocupadas", waitMs: 2800 },
      { label: "Click en la mesa donde tomarás el pedido", waitMs: 2400 },
      { label: "Selecciona productos del menú en el panel de la derecha", waitMs: 2400 },
      { label: "Cada producto se agrega al carrito. Puedes ajustar cantidad y observaciones", waitMs: 2800 },
      { label: "Click en 'Confirmar pedido'. Va automáticamente a cocina", waitMs: 2400 },
    ],
  },
  "take-delivery": {
    id: "take-delivery",
    title: "Tomar un pedido de domicilio",
    summary:
      "Flujo de domicilio: productos primero, luego datos del cliente y dirección.",
    steps: [
      { label: "Vamos a Tomar Domicilio", targetTab: "tomar-domicilio", waitMs: 1200 },
      { label: "PASO 1: agrega productos al carrito desde el menú", waitMs: 2400 },
      { label: "Cada producto puede tener adiciones, observaciones y cantidad", waitMs: 2800 },
      { label: "PASO 2: click en 'Continuar'. Pasarás a datos del cliente", waitMs: 2400 },
      { label: "Busca un cliente registrado o crea uno nuevo (nombre, teléfono, dirección)", waitMs: 3000 },
      { label: "Click en 'Confirmar pedido'. Aparece en Cocina + Entregas", waitMs: 2400 },
    ],
  },
  "edit-order-items": {
    id: "edit-order-items",
    title: "Corregir items de un pedido en curso",
    summary: "En 'Corregir Pedido' puedes ajustar cantidades, eliminar items o reemplazar productos.",
    steps: [
      { label: "Abrimos Corregir Pedido", targetTab: "corregir-pedido", waitMs: 1200 },
      { label: "Click en la mesa con el pedido a corregir", waitMs: 2400 },
      { label: "Verás todos los items del pedido con botones +/− y papelera", waitMs: 2400 },
      { label: "+ y − ajustan cantidad; papelera elimina (item se tacha en rojo)", waitMs: 2800 },
      { label: "↺ permite reemplazar el producto por otro distinto", waitMs: 2400 },
      { label: "Al terminar, click en 'Confirmar cambios' al fondo. Cocina recibe la corrección con diff visual", waitMs: 2800 },
    ],
  },
  "kitchen-mark-ready": {
    id: "kitchen-mark-ready",
    title: "Marcar pedido como listo (cocina)",
    summary: "El chef marca cada ticket como listo al terminar — el mesero recibe notificación.",
    steps: [
      { label: "Vamos a Cocina", targetTab: "cocina", waitMs: 1200 },
      { label: "Aquí ves los tickets activos en cola por antigüedad", waitMs: 2200 },
      { label: "Click en 'Listo' del ticket que terminaste", waitMs: 2400 },
      { label: "El pedido se mueve al bloque 'Esperando mesero' (azul)", waitMs: 2400 },
    ],
  },
  "waiter-mark-delivered": {
    id: "waiter-mark-delivered",
    title: "Confirmar entrega de un pedido en mesa",
    summary: "Cuando el plato llega a la mesa, el mesero lo marca como entregado.",
    steps: [
      { label: "Abre Cocina (los meseros también pueden ver desde aquí)", targetTab: "cocina", waitMs: 1200 },
      { label: "Encuentra el pedido en estado 'Esperando mesero' (azul)", waitMs: 2400 },
      { label: "Click en 'Entregar'. Pasa a 'Entregado en mesa' (verde)", waitMs: 2400 },
    ],
  },

  // ─── CAJA / COBRO ─────────────────────────────────────────────────────
  "open-cash-session": {
    id: "open-cash-session",
    title: "Abrir caja del día",
    summary:
      "Antes de cobrar pedidos, hay que abrir la sesión de caja con el saldo inicial en efectivo.",
    steps: [
      { label: "Vamos a Apertura/Cierre", targetTab: "sesiones-caja", waitMs: 1200 },
      { label: "Selecciona la caja registradora (panel izquierdo)", waitMs: 2400 },
      { label: "Si la caja está cerrada, verás un botón 'Abrir caja'", waitMs: 2400 },
      { label: "Click en 'Abrir caja' y escribe el saldo inicial en efectivo (lo que tienes físicamente)", waitMs: 2800 },
      { label: "Confirma. La caja queda abierta y lista para cobrar pedidos", waitMs: 2400 },
    ],
  },
  "close-cash-session": {
    id: "close-cash-session",
    title: "Cerrar caja del día",
    summary:
      "Al final del turno, cuenta el efectivo físico, ingrésalo y cierra la caja.",
    steps: [
      { label: "Vamos a Apertura/Cierre", targetTab: "sesiones-caja", waitMs: 1200 },
      { label: "Selecciona la caja con sesión abierta", waitMs: 2400 },
      { label: "Verás el resumen del turno: ventas por método, total esperado en efectivo", waitMs: 2800 },
      { label: "Click en 'Cerrar caja'. Cuenta el efectivo físico real e ingresa el total", waitMs: 3000 },
      { label: "El sistema calcula la diferencia automáticamente. Confirma para cerrar el día", waitMs: 2800 },
    ],
  },
  "charge-order": {
    id: "charge-order",
    title: "Cobrar un pedido",
    summary: "Para cobrar pedidos en mesa: tab Caja → mesa → método de pago → confirmar.",
    steps: [
      { label: "Vamos a Cobrar Orden", targetTab: "caja", waitMs: 1200 },
      { label: "Click en la mesa que quieres cobrar", waitMs: 2400 },
      { label: "Verás todos los items del pedido (o las múltiples rondas) con su total", waitMs: 2800 },
      { label: "Selecciona método de pago: efectivo, tarjeta, transferencia o cortesía", waitMs: 2800 },
      { label: "Si es transferencia, elige el intermediario (Nequi, Daviplata, etc.)", waitMs: 2400 },
      { label: "Si es efectivo, usa la calculadora de cambio integrada", waitMs: 2400 },
      { label: "Click en 'Cobrar'. El pedido cierra y aparece prompt de facturación opcional", waitMs: 2800 },
    ],
  },
  "charge-delivery": {
    id: "charge-delivery",
    title: "Cobrar un pedido de domicilio",
    summary:
      "Cobrar Domicilio lista los pedidos delivery listos para cobro con el mismo flujo de caja.",
    steps: [
      { label: "Vamos a Cobrar Domicilio", targetTab: "cobrar-domicilio", waitMs: 1200 },
      { label: "Selecciona el pedido a cobrar", waitMs: 2400 },
      { label: "Verifica items, dirección y total con el cliente", waitMs: 2800 },
      { label: "Selecciona método de pago y confirma", waitMs: 2400 },
    ],
  },
  "print-receipt": {
    id: "print-receipt",
    title: "Imprimir ticket térmico (80mm)",
    summary:
      "Tickets se imprimen desde el navegador. Para reimpresión, ve a Órdenes Completadas.",
    steps: [
      { label: "Para reimprimir: vamos a Órdenes Completadas", targetTab: "pedidos-completados", waitMs: 1500 },
      { label: "Encuentra el pedido en la lista (búsqueda por mesa o ID)", waitMs: 2400 },
      { label: "Click en el ícono 🖨️ de la fila. Se abre popup 80mm", waitMs: 2400 },
      { label: "Selecciona tu impresora térmica en el diálogo de impresión y confirma", waitMs: 2400 },
    ],
  },

  // ─── MESAS ────────────────────────────────────────────────────────────
  "create-table": {
    id: "create-table",
    title: "Crear una mesa nueva",
    summary: "CRUD de mesas desde la pestaña Mesas.",
    steps: [
      { label: "Vamos a Mesas", targetTab: "mesas", waitMs: 1200 },
      { label: "Click en 'Nueva mesa'", selector: "[data-copilot-action='create-table']", click: true, waitMs: 2000 },
      { label: "Llena nombre (ej. Mesa 12), capacidad (sillas) y zona opcional (Terraza, Piso 2)", waitMs: 2800 },
      { label: "Confirma. La mesa queda lista para recibir pedidos", waitMs: 2200 },
    ],
  },

  // ─── RESERVAS ─────────────────────────────────────────────────────────
  "create-reservation": {
    id: "create-reservation",
    title: "Crear una reserva manualmente",
    summary: "Calendario semanal con drag & drop para asignar mesas a clientes.",
    steps: [
      { label: "Abrimos Reservas", targetTab: "reservas", waitMs: 1200 },
      { label: "Verás el calendario de la semana actual con las mesas como filas", waitMs: 2400 },
      { label: "Click en una celda libre del horario para abrir el formulario de reserva", waitMs: 2800 },
      { label: "Busca el cliente (o créalo) y completa fecha, hora, cantidad de personas", waitMs: 3000 },
      { label: "Confirma. Aparece como bloque en la celda elegida", waitMs: 2200 },
      { label: "Para reasignar a otra mesa, arrastra el bloque (drag & drop)", waitMs: 2400 },
    ],
  },

  // ─── CLIENTES ─────────────────────────────────────────────────────────
  "create-client": {
    id: "create-client",
    title: "Registrar un cliente nuevo",
    summary: "Pestaña Clientes → CRUD básico.",
    steps: [
      { label: "Vamos a Clientes", targetTab: "clientes", waitMs: 1200 },
      { label: "Click en 'Nuevo cliente'", selector: "[data-copilot-action='create-client']", click: true, waitMs: 2000 },
      { label: "Llena nombre, teléfono, email y notas opcionales", waitMs: 2800 },
      { label: "Confirma. El cliente queda disponible para reservas y domicilios", waitMs: 2200 },
    ],
  },
  "pay-credit": {
    id: "pay-credit",
    title: "Registrar abono a cuenta de crédito",
    summary: "Cuentas de crédito muestran clientes con deuda y permite registrar abonos.",
    steps: [
      { label: "Vamos a Cuentas de crédito", targetTab: "cuentas-credito", waitMs: 1200 },
      { label: "Encuentra al cliente con deuda en el listado", waitMs: 2400 },
      { label: "Click sobre la cuenta para ver el detalle de movimientos", waitMs: 2400 },
      { label: "Click en 'Registrar abono', escribe monto y método de pago", waitMs: 2800 },
      { label: "Confirma. El saldo pendiente se reduce al instante", waitMs: 2400 },
    ],
  },

  // ─── PERSONAL ─────────────────────────────────────────────────────────
  "invite-member": {
    id: "invite-member",
    title: "Invitar a un miembro del personal",
    summary: "Email + rol → llega un correo con link para que se una.",
    steps: [
      { label: "Vamos a Personal", targetTab: "personal", waitMs: 1200 },
      { label: "Click en 'Invitar personal'", selector: "[data-copilot-action='invite-member']", click: true, waitMs: 2000 },
      { label: "Escribe el email y elige el rol (mesero, chef, cajero, admin, domicilios)", waitMs: 2800 },
      { label: "Confirma. La persona recibe un correo con un link de invitación", waitMs: 2400 },
      { label: "Cuando la acepta, aparece en tu lista con ese rol", waitMs: 2200 },
    ],
  },
  "update-member-role": {
    id: "update-member-role",
    title: "Cambiar el rol de un miembro",
    summary: "Click en el menú del miembro → Editar rol.",
    steps: [
      { label: "Vamos a Personal", targetTab: "personal", waitMs: 1200 },
      { label: "Encuentra el miembro y click en su menú de tres puntos (⋮)", waitMs: 2400 },
      { label: "Selecciona 'Cambiar rol'", waitMs: 2200 },
      { label: "Elige el nuevo rol y confirma. El miembro ve los cambios al refrescar", waitMs: 2400 },
    ],
  },
  "accept-invitation": {
    id: "accept-invitation",
    title: "Aceptar invitación recibida",
    summary: "Las invitaciones a otros restaurantes llegan al ícono de campana en la navbar.",
    steps: [
      { label: "Click en el ícono 🔔 en la barra superior derecha", waitMs: 2400 },
      { label: "Verás las invitaciones pendientes con el restaurante y rol", waitMs: 2400 },
      { label: "Click en 'Aceptar' al lado de la que quieras", waitMs: 2400 },
      { label: "El restaurante aparecerá en tu listado al recargar", waitMs: 2200 },
    ],
  },

  // ─── ALERTAS / STOCK ──────────────────────────────────────────────────
  "manage-alerts": {
    id: "manage-alerts",
    title: "Gestionar alertas operativas",
    summary: "Alertas de stock bajo, sin stock y restock.",
    steps: [
      { label: "Vamos a Alertas", targetTab: "alertas", waitMs: 1200 },
      { label: "Verás todas las alertas con tipo, producto y timestamp", waitMs: 2400 },
      { label: "Click en una alerta para marcarla como leída individualmente", waitMs: 2400 },
      { label: "O usa 'Marcar todas como leídas' al inicio del listado", waitMs: 2400 },
    ],
  },

  // ─── PREFERENCIAS / RESTAURANTE ───────────────────────────────────────
  "configure-menu-design": {
    id: "configure-menu-design",
    title: "Cambiar diseño y paleta de la carta digital",
    summary:
      "11 diseños + 11 paletas para la carta pública. Vista previa en vivo.",
    steps: [
      { label: "Vamos a Preferencias", targetTab: "preferencias", waitMs: 1200 },
      { label: "Busca la sección 'Carta digital'", waitMs: 2400 },
      { label: "Click en cualquier diseño para previsualizarlo", waitMs: 2800 },
      { label: "Selecciona también una paleta (o usa 'paleta automática' desde tu logo)", waitMs: 2800 },
      { label: "Los cambios se aplican al instante en la URL pública", waitMs: 2200 },
    ],
  },
  "upload-restaurant-image": {
    id: "upload-restaurant-image",
    title: "Subir logo o banner del restaurante",
    summary: "Hover sobre la imagen actual y click en 'Cambiar'.",
    steps: [
      { label: "Vamos a Información", targetTab: "info", waitMs: 1200 },
      { label: "Pasa el cursor sobre el logo o banner. Aparece botón 'Cambiar'", waitMs: 2800 },
      { label: "Click y selecciona la imagen desde tu equipo", waitMs: 2400 },
      { label: "Se sube a Cloudinary automáticamente y queda aplicada", waitMs: 2400 },
    ],
  },

  // ─── INTERMEDIARIOS ───────────────────────────────────────────────────
  "create-bank-transfer": {
    id: "create-bank-transfer",
    title: "Configurar un intermediario bancario",
    summary: "Para recibir pagos por transferencia (Nequi, Daviplata, etc.)",
    steps: [
      { label: "Vamos a Intermediarios", targetTab: "intermediarios", waitMs: 1200 },
      { label: "Click en 'Agregar intermediario'", selector: "[data-copilot-action='create-bank-transfer']", click: true, waitMs: 2000 },
      { label: "Escribe nombre (ej. Nequi) y datos de la cuenta (titular, número)", waitMs: 2800 },
      { label: "Confirma. Aparecerá disponible al cobrar pedidos por transferencia", waitMs: 2400 },
    ],
  },

  // ─── CUENTA PERSONAL ──────────────────────────────────────────────────
  "change-password": {
    id: "change-password",
    title: "Cambiar contraseña personal",
    summary: "Tu cuenta se gestiona desde tu perfil, no desde un restaurante.",
    steps: [
      { label: "Click en el ícono de tu avatar en la barra superior", waitMs: 2400 },
      { label: "Selecciona 'Mi perfil'", waitMs: 2200 },
      { label: "En 'Seguridad', ingresa tu contraseña actual y la nueva", waitMs: 2800 },
      { label: "Confirma. Tu sesión sigue activa con la nueva contraseña", waitMs: 2400 },
    ],
  },

  // ─── ANALÍTICA ────────────────────────────────────────────────────────
  "view-analytics": {
    id: "view-analytics",
    title: "Ver reportes analíticos",
    summary:
      "10 reportes disponibles bajo el grupo Analítica. Cada uno con date range picker.",
    steps: [
      { label: "Abrimos el Dashboard analítico", targetTab: "dashboard-analitico", waitMs: 1200 },
      { label: "Aquí ves KPIs generales y resumen del período", waitMs: 2400 },
      { label: "El selector de fechas arriba a la derecha permite cambiar el período", waitMs: 2800 },
      { label: "En el sidebar, dentro del grupo 'Analítica' tienes reportes detallados por área", waitMs: 2800 },
      { label: "Cada reporte muestra KPIs + gráficas + tablas con top items y exportable", waitMs: 2400 },
    ],
  },
};

const TUTORIAL_IDS = Object.keys(TUTORIALS);

export const guideUserSkill: Skill = {
  id: "guideUser",
  description:
    "Guía visual al usuario en cualquier acción del sistema. Útil cuando preguntan '¿cómo hago X?' o cuando una skill no puede ejecutar la acción y debes enseñarle el camino. " +
    "El copiloto narra paso a paso con cursor virtual + highlights y luego el user completa. " +
    "Tutoriales: " +
    TUTORIAL_IDS.join(", "),
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      action: {
        type: "string",
        enum: TUTORIAL_IDS,
        description:
          "ID del tutorial. Cada uno corresponde a una acción concreta del sistema.",
      },
    },
    required: ["restaurantId", "action"],
  },
  async run(args, ctx) {
    const restaurantId = String(args.restaurantId ?? "");
    const action = String(args.action ?? "");
    const tutorial = TUTORIALS[action];
    if (!tutorial) {
      return {
        ok: false,
        error: `Tutorial desconocido: ${action}. Disponibles: ${TUTORIAL_IDS.join(", ")}`,
      };
    }

    if (!ctx.follow.enabled) {
      const stepsText = tutorial.steps
        .map((s, i) => `${i + 1}. ${s.label}`)
        .join("\n");
      return {
        ok: true,
        result: {
          mode: "no-follow",
          title: tutorial.title,
          steps: tutorial.steps.map((s) => s.label),
          summary: tutorial.summary,
          followDisabled: true,
        },
        humanSummary:
          `## ${tutorial.title}\n\n` +
          stepsText +
          `\n\n_${tutorial.summary}_\n\n` +
          `> 💡 **Tip**: activa el modo **guía visual** (toggle "follow" en el header del chat) y pídeme la misma guía de nuevo. Te mostraré cada paso con un cursor virtual y te llevaré a la pestaña correcta paso a paso.`,
      };
    }

    // Modo follow: walkthrough visual
    let lastTab: string | undefined;
    const total = tutorial.steps.length;
    for (let i = 0; i < total; i++) {
      const step = tutorial.steps[i];

      // Navegación a tab si aplica
      if (step.targetTab && step.targetTab !== lastTab) {
        await ctx.follow.step(`Voy a abrir "${step.targetTab}"…`, {
          durationMs: 500,
        });
        await ctx.follow.clickAt(`[data-copilot-tab="${step.targetTab}"]`, {
          durationMs: 700,
          label: step.targetTab,
        });
        if (restaurantId) {
          ctx.router.push(
            `/dashboard/restaurantes/${restaurantId}?tab=${encodeURIComponent(step.targetTab)}`,
          );
        }
        await sleep(1100); // espera al render del tab
        lastTab = step.targetTab;
      }

      // Banner del paso siempre visible
      const stepNumber = `Paso ${i + 1} de ${total}`;
      const labelWithNum = `${stepNumber} · ${step.label}`;

      // Highlight / point / click si hay selector
      const highlightTarget = step.highlightSelector ?? step.selector;
      if (highlightTarget && step.click) {
        // Click real: animación visual + dispara evento DOM (abre modales)
        void ctx.follow.step(labelWithNum, {
          durationMs: (step.waitMs ?? 2000) + 1200,
        });
        await ctx.follow.realClickAt(highlightTarget, {
          durationMs: step.waitMs ?? 2000,
          label: step.label,
        });
      } else if (highlightTarget) {
        // Solo visual: cursor + highlight, sin evento DOM
        void ctx.follow.step(labelWithNum, {
          durationMs: (step.waitMs ?? 2400) + 1200,
        });
        await ctx.follow.pointAt(highlightTarget, { durationMs: 600 });
        await ctx.follow.highlight(highlightTarget, {
          durationMs: step.waitMs ?? 2400,
        });
      } else {
        // Sin selector: el banner es el único feedback. Lo mantenemos
        // visible más tiempo para que el user pueda leer la instrucción.
        // Mínimo 3.5s para steps con texto largo.
        const readingTime = Math.max(
          3500,
          step.waitMs ?? 0,
          // ~50 ms por carácter para asegurar lectura
          step.label.length * 55,
        );
        await ctx.follow.step(labelWithNum, { durationMs: readingTime });
      }
    }

    await ctx.follow.step("✓ Listo — ahora completa el formulario tú mismo", {
      durationMs: 2500,
      spinner: false,
    });
    ctx.follow.clear();

    return {
      ok: true,
      result: {
        mode: "follow",
        guided_only: true,
        action_completed: false,
        title: tutorial.title,
        steps: tutorial.steps.map((s) => s.label),
      },
      humanSummary:
        `[GUÍA VISUAL COMPLETADA — NO SE EJECUTÓ NINGUNA ACCIÓN]\n\n` +
        `Mostré los ${total} pasos visualmente. El usuario TODAVÍA DEBE completar la acción por su cuenta: ` +
        `el modal/formulario ya está abierto (si aplica), pero rellenar y confirmar lo hace el usuario.\n\n` +
        `NO reportes la acción como completada. Dile al usuario que ya tiene el formulario abierto ` +
        `y que lo complete él mismo.`,
    };
  },
};
